import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi } from '../../../api/trips.api';
import { toast } from '../../ui/Toast';
import Spinner from '../../ui/Spinner';

interface Props {
  tripId: string;
  onAdded: () => void;
}

/** ADR-0021 — a traveller by name only, for splitting expenses before they sign up. */
export default function AddPlaceholderForm({ tripId, onAdded }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await tripsApi.createPlaceholder(tripId, trimmed);
      setName('');
      toast(t('trip.participants.placeholder.toast.added', { name: trimmed }), 'success');
      onAdded();
    } catch {
      toast(t('trip.participants.placeholder.toast.addFailed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pt-4 border-t border-slate-100 dark:border-slate-700/60">
      <p className="label mb-2">{t('trip.participants.placeholder.addTitle')}</p>
      <div className="flex gap-2">
        <input
          className="input flex-1 min-w-0"
          placeholder={t('trip.participants.placeholder.namePlaceholder')}
          value={name}
          maxLength={60}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button onClick={handleAdd} className="btn-secondary shrink-0" disabled={saving || !name.trim()}>
          {saving ? <Spinner size="sm" /> : t('trip.participants.placeholder.add')}
        </button>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
        {t('trip.participants.placeholder.addHint')}
      </p>
    </div>
  );
}
